// Date helpers for the calendar. All dates we exchange with the SQL function
// are 'YYYY-MM-DD' interpreted as Europe/Amsterdam days. Browser Date math is
// in the user's local zone — we go via Intl when we need the venue's day.

export const VENUE_TZ = 'Europe/Amsterdam';

export function toVenueDateString(d: Date): string {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: VENUE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return dtf.format(d); // 'YYYY-MM-DD'
}

export function parseVenueDate(s: string): Date {
  // Treat 'YYYY-MM-DD' as midnight in Europe/Amsterdam.
  const [y, m, d] = s.split('-').map(Number);
  // Build a Date that represents midnight venue-time. Use a quick UTC midnight
  // then shift by the venue offset for that wall-time.
  const tentative = new Date(Date.UTC(y!, (m! - 1), d!, 0, 0, 0));
  const offset = venueOffsetMinutes(tentative);
  return new Date(tentative.getTime() - offset * 60_000);
}

export function venueOffsetMinutes(d: Date): number {
  // Intl.formatToParts THROWS on an Invalid Date (e.g. a malformed input
  // string upstream) — degrade to UTC instead of taking the page down.
  if (Number.isNaN(d.getTime())) return 0;
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: VENUE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  const utcMs = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') === 24 ? 0 : get('hour'),
    get('minute'),
    get('second'),
  );
  return (utcMs - d.getTime()) / 60_000;
}

// Pure calendar arithmetic: 'YYYY-MM-DD' plus n days needs no timezone at all.
// The previous implementation (venue midnight + n*24h) stalled on the 25-hour
// DST fall-back day — Oct 25 + 1 day landed on Oct 25 again, so any daily
// stepping loop (period arrows, chart bucket fill) froze there.
export function addDaysVenue(s: string, n: number): string {
  const [y, m, d] = s.split('-').map(Number);
  const next = new Date(Date.UTC(y!, m! - 1, d! + n));
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  return `${next.getUTCFullYear()}-${mm}-${dd}`;
}

// Monday of the venue-week containing the given date string.
export function venueWeekStart(s: string): string {
  const d = parseVenueDate(s);
  // ISO week starts Monday. JS getUTCDay returns 0=Sunday..6=Saturday.
  // We need the day-of-week IN VENUE TZ — read it via Intl.
  const dow = new Intl.DateTimeFormat('en-US', {
    timeZone: VENUE_TZ,
    weekday: 'short',
  }).format(d); // 'Mon', 'Tue', ...
  const map: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  const offset = map[dow] ?? 0;
  return addDaysVenue(s, -offset);
}

export function rangeDays(fromIncl: string, count: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) out.push(addDaysVenue(fromIncl, i));
  return out;
}

export function todayVenue(): string {
  return toVenueDateString(new Date());
}

export function formatVenueLong(s: string): string {
  if (Number.isNaN(parseVenueDate(s).getTime())) return s;
  return parseVenueDate(s).toLocaleDateString('nl-NL', {
    timeZone: VENUE_TZ,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatVenueShort(s: string): string {
  if (Number.isNaN(parseVenueDate(s).getTime())) return s;
  return parseVenueDate(s).toLocaleDateString('nl-NL', {
    timeZone: VENUE_TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

export function venueWeekLabel(weekStart: string): string {
  const d = parseVenueDate(weekStart);
  if (Number.isNaN(d.getTime())) return weekStart;
  const end = new Date(d.getTime() + 6 * 86_400_000);
  const sameMonth =
    d.toLocaleDateString('nl-NL', { timeZone: VENUE_TZ, month: 'short' }) ===
    end.toLocaleDateString('nl-NL', { timeZone: VENUE_TZ, month: 'short' });
  const fromStr = d.toLocaleDateString('nl-NL', {
    timeZone: VENUE_TZ,
    day: 'numeric',
    month: sameMonth ? undefined : 'short',
  });
  const toStr = end.toLocaleDateString('nl-NL', {
    timeZone: VENUE_TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `${fromStr} – ${toStr}`;
}
